import { useEffect, useMemo, useState, useCallback } from "react";

const emptyUserDetails = { name: "", email: "", adminEmail: "" };

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || fallback;
}

export function useAuthVerification({
  requestType,
  token,
  invitationToken,
  subsidiaryToken,
  enabled = true,
  requiresInvitation,
  verifyUser,
  verifySubUser,
  verifySubsidiaryUser,
  scrubSensitiveParamsFromUrl,
}) {
  const [message, setMessage] = useState("");
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(emptyUserDetails);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationVerified, setInvitationVerified] = useState(false);
  const [invitationVerificationFailed, setInvitationVerificationFailed] =
    useState(false);
  const [persistedInvitationToken, setPersistedInvitationToken] =
    useState(null);

  const clearAuthFeedback = useCallback(() => {
    setMessage("");
    setVerificationStatus(null);
    setVerificationLoading(false);
  }, []);

  const effectiveInvitationToken =
    persistedInvitationToken ?? invitationToken ?? null;

  const verificationKind = useMemo(() => {
    if (requestType === "verify_admin" && token) return "admin";
    if (requestType === "verify_subsidiary" && subsidiaryToken) {
      return "subsidiary";
    }
    if (requiresInvitation) return "invitation";
    return null;
  }, [requestType, token, subsidiaryToken, requiresInvitation]);

  useEffect(() => {
    if (requestType !== "register") {
      setInvitationLoading(false);
      setInvitationVerified(false);
      setInvitationVerificationFailed(false);
      setUserDetails(emptyUserDetails);
      setPersistedInvitationToken(null);
    }
  }, [requestType]);

  useEffect(() => {
    if (!enabled) return;

    const ac = new AbortController();
    const { signal } = ac;

    const run = async () => {
      if (verificationKind === "admin") {
        setVerificationLoading(true);
        setVerificationStatus("loading");
        setMessage("Verifying your email...");
        try {
          await verifyUser(JSON.stringify({ token }), { signal });
          if (signal.aborted) return;
          setVerificationStatus("success");
          setMessage(
            "Your email has been successfully verified! You can now log in."
          );
        } catch (error) {
          if (signal.aborted) return;
          setVerificationStatus("error");
          setMessage(
            getErrorMessage(error, "Invalid or expired verification link.")
          );
        } finally {
          if (!signal.aborted) setVerificationLoading(false);
        }
        return;
      }

      if (verificationKind === "subsidiary") {
        setVerificationLoading(true);
        setVerificationStatus("loading");
        setMessage("Verifying your subsidiary account...");
        try {
          await verifySubsidiaryUser({ token: subsidiaryToken }, { signal });
          if (signal.aborted) return;
          scrubSensitiveParamsFromUrl?.({ nextRequestType: "login" });
          setVerificationStatus("success");
          setMessage("Your email has been successfully verified.");
        } catch (error) {
          if (signal.aborted) return;
          setVerificationStatus("error");
          setMessage("Invalid or expired invitation token.");
        } finally {
          if (!signal.aborted) setVerificationLoading(false);
        }
        return;
      }

      if (verificationKind === "invitation") {
        setInvitationLoading(true);
        setInvitationVerified(false);
        setInvitationVerificationFailed(false);
        try {
          const result = await verifySubUser(
            { token: invitationToken },
            { signal }
          );
          if (signal.aborted) return;
          setPersistedInvitationToken(invitationToken);
          scrubSensitiveParamsFromUrl?.({
            nextRequestType: "register",
            dropInvitationTokenOnly: true,
          });
          setUserDetails({
            name: result.name || "",
            email: result.email || "",
            adminEmail: result.adminEmail || "",
          });
          setInvitationVerified(true);
          setVerificationStatus("success");
          setMessage(`Invited by ${result.adminEmail}`);
        } catch (error) {
          if (signal.aborted) return;
          setInvitationVerified(false);
          setInvitationVerificationFailed(true);
          setVerificationStatus("error");
          setMessage(
            getErrorMessage(
              error,
              "This invitation link is invalid or has expired. Check the link or ask your admin for a new invitation."
            )
          );
        } finally {
          if (!signal.aborted) setInvitationLoading(false);
        }
      }
    };

    run();

    return () => ac.abort();
  }, [
    enabled,
    verificationKind,
    token,
    invitationToken,
    subsidiaryToken,
    verifyUser,
    verifySubUser,
    verifySubsidiaryUser,
    scrubSensitiveParamsFromUrl,
  ]);

  return {
    message,
    setMessage,
    verificationStatus,
    verificationLoading,
    userDetails,
    invitationLoading,
    invitationVerified,
    invitationVerificationFailed,
    effectiveInvitationToken,
    clearAuthFeedback,
  };
}
